import { useCallback, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import useOnboardingProcess from '@hooks/useOnboardingProcess';
import { useAssetViewerState } from '@hooks/useAssetViewerState';
import wait from '@helpers/wait';
import { useResponsive } from '@hooks/useResponsive';

export default function useMediaViewerReviewAssetTour({
  isAssetReady,
  isAudio,
  isPreparing,
  isUnsupported,
  assetWorkspaceId,
  setSelectedCollapse
}: {
  isAssetReady: boolean;
  isAudio: boolean;
  isPreparing: boolean;
  isUnsupported: boolean;
  assetWorkspaceId?: string;
  setSelectedCollapse?: (collapse: string | null) => void;
}) {
  const { isLgWidth } = useResponsive();
  const { onStartTour } = useOnboardingProcess();
  const [searchParams] = useSearchParams();
  const { selectComment, setCanvasMode, setDrawToolsShape } =
    useAssetViewerState();
  const startTour = searchParams.get('startTour');

  const onStartTourReviewAsset = useCallback(() => {
    onStartTour({
      type: 'review-asset',
      onClickNext: async (currentStep, isExitTour) => {
        if (isExitTour) return;
        if (currentStep === 0) {
          selectComment('new');
          setCanvasMode('edit');
          setDrawToolsShape('pin');
          return;
        }
        if (currentStep === 1) {
          setDrawToolsShape('cursor');
          setCanvasMode('view');
          setSelectedCollapse?.('comments');
          await wait(500);
        }
      },
      onClickPrev: (currentStep, isExitTour) => {
        if (isExitTour) return;
        if (currentStep === 1) {
          setCanvasMode('view');
          return;
        }
        if (currentStep === 2) {
          selectComment('new');
          setCanvasMode('edit');
          setDrawToolsShape('pin');
        }
      }
    });
  }, [
    onStartTour,
    selectComment,
    setCanvasMode,
    setDrawToolsShape,
    setSelectedCollapse
  ]);

  useEffect(() => {
    if (!isLgWidth) return;
    if (startTour !== 'review-asset') return;
    if (!assetWorkspaceId) return;
    if (isAudio || isUnsupported) return;
    if (isPreparing) return;
    if (!isAssetReady) return;
    onStartTourReviewAsset();
  }, [
    startTour,
    assetWorkspaceId,
    isAssetReady,
    isAudio,
    isPreparing,
    isUnsupported,
    onStartTourReviewAsset,
    isLgWidth
  ]);

  return null;
}
